import { Location } from "@angular/common";
import { Component, OnInit, ChangeDetectorRef, Input, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { BaseComponent, UserService, ProductService, SimpleProduct, AdminService, WindowService, EventBusService, ProductSnapshot, HelperService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { AdminAddNewProductComponent, GenericAlertComponent } from "../../modals";
import { format } from "date-fns";
import { TranslateService } from "@ngx-translate/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-admin-pending-giveaways",
  templateUrl: "pending-giveaways.component.html",
  standalone: false,
})
export class AdminPendingGiveawaysComponent extends BaseComponent implements OnInit {
  @Input() userDetails: any;
  @Input() pendingAssets: any[] = [];
  public queryValue: string = "";
  public searching: boolean = false;
  public loading: boolean = false;
  public assetDetailMap: { [key: string]: any } = {};
  constructor(
    userService: UserService,
    public productService: ProductService,
    public adminService: AdminService,
    public appService: AppService,
    private _activatedRoute: ActivatedRoute,
    public router: Router,
    public location: Location,
    private _win: WindowService,
    private _eventbusService: EventBusService,
    private _translate: TranslateService,
    public helperService: HelperService
  ) {
    super(userService);
  }

  ngOnInit() {
    const queryParams = this._activatedRoute.snapshot.queryParams;
    const userEmail = queryParams["userEmail"] || this.userDetails?.email;
    if (userEmail?.trim()) {
      this.queryValue = userEmail.trim();
      this.search();
    } else {
      this.loadAll();
    }
  }

  loadAll() {
    this.loading = true;
    this.adminService.get(`product/giveaways/pending`).subscribe({
      next: (result: any) => {
        this.pendingAssets = result?.items?.length ? result?.items : [];
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.appService.alert(this._translate.instant("admin_portal.error_fetching_giveaways"));
      },
    });
  }

  collapsedChanged(args: { item: any; state: "expanded" | "collapsed" }) {
    if (args.state == "expanded") {
      let pendingAsset = this.pendingAssets.find((pa) => pa.asset.assetId == args.item.asset.assetId);
      if (!this.assetDetailMap[args.item.asset.assetId]) {
        pendingAsset.loading = true;
        this.adminService.get(`product/giveaways/pending/${args.item.asset.assetId}`).subscribe({
          next: (result: any) => {
            pendingAsset.loading = false;
            result.allGiftsRedeemed = result.giveaways?.length && result.giveaways.every((g) => g.redeemed);
            this.assetDetailMap[args.item.asset.assetId] = result;
          },
          error: (error) => {
            this.appService.alert(this._translate.instant("admin_portal.error_fetching_giveaway"));
          },
        });
      }
    }
  }

  addGiftToAccount(gift, assetDetail) {
    this.assetDetailMap[assetDetail.asset.id].redeeming = true;
    this.productService.redeemProduct({ id: gift.gift_id, groupId: assetDetail.asset.id, userId: assetDetail.user.id, markPendingFulfilled: true }).subscribe((result) => {
      this.productService.getProducts(true).subscribe((result) => {
        this.assetDetailMap[assetDetail.asset.id].redeeming = false;
        gift.redeemed = true;
        const detail = this.assetDetailMap[assetDetail.asset.id];
        detail.allGiftsRedeemed = detail.giveaways.every((g) => g.redeemed);
        if (detail.allGiftsRedeemed) {
          this.pendingAssets = this.pendingAssets.filter((pa) => pa.asset.assetId !== assetDetail.asset.id);
          delete this.assetDetailMap[assetDetail.asset.id];
        }
      });
    });
  }

  rejectGift(gift, assetDetail) {
    this.assetDetailMap[assetDetail.asset.id].rejecting = true;
    this.adminService.put(`user/${assetDetail.user.id}/asset/${assetDetail.asset.id}`, { pendingGiveAway: "rejected" }).subscribe((result) => {
      this.assetDetailMap[assetDetail.asset.id].rejecting = false;
      const detail = this.assetDetailMap[assetDetail.asset.id];
      detail.giveaways = detail.giveaways.filter((g) => g !== gift);
      if (detail.giveaways.length === 0) {
        this.pendingAssets = this.pendingAssets.filter((pa) => pa.asset.assetId !== assetDetail.asset.id);
        delete this.assetDetailMap[assetDetail.asset.id];
      }
    });
  }

  markFulfilled(assetDetail) {
    this.assetDetailMap[assetDetail.asset.id].fulfilling = true;
    this.adminService.put(`user/${assetDetail.user.id}/asset/${assetDetail.asset.id}`, { pendingGiveAway: "fulfilled" }).subscribe((result) => {
      this.pendingAssets = this.pendingAssets.filter((pa) => pa.asset.assetId !== assetDetail.asset.id);
      delete this.assetDetailMap[assetDetail.asset.id];
    });
  }

  search() {
    if (!this.queryValue?.trim()) {
      this.loadAll();
      return;
    }
    if (!this.userDetails) {
      this.router.navigate([], {
        relativeTo: this._activatedRoute,
        queryParams: { userEmail: this.queryValue.trim() },
        replaceUrl: true,
      });
    }
    this.searching = true;
    this.loading = true;
    this.adminService.get(`product/giveaways/pending?email=${encodeURIComponent(this.queryValue.trim())}`).subscribe({
      next: (result: any) => {
        this.pendingAssets = result?.items?.length ? result?.items : [];
        this.searching = false;
        this.loading = false;
      },
      error: (error) => {
        this.searching = false;
        this.loading = false;
        this.pendingAssets = [];
        this.appService.alert(this._translate.instant("admin_portal.user_not_found_or_no_giveaways"));
      },
    });
  }
}
